<template>
    <div class="debug-flute-container">
        <h1 class="text-h3 text-primary">🎵 Flute Fingering Component Demo</h1>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Fingering & Highlights</h2>

                <div
                    style="
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;
                    "
                >
                    <!-- Fingering Controls (Left Column) -->
                    <div>
                        <h3 class="text-h6" style="margin-top: 0">Fingering</h3>
                        <div class="key-section">
                            <h3 class="text-h6">Main Holes</h3>
                            <div class="key-group">
                                <div
                                    class="key-control"
                                    v-for="key in mainHoles"
                                    :key="key"
                                >
                                    <label>{{ key.toUpperCase() }}</label>
                                    <div class="button-group">
                                        <button
                                            @click="setKey(key, 0)"
                                            :class="{
                                                active: fingering[key] === 0,
                                            }"
                                            class="state-btn open"
                                        >
                                            Open
                                        </button>
                                        <button
                                            @click="setKey(key, 0.5)"
                                            :class="{
                                                active: fingering[key] === 0.5,
                                            }"
                                            class="state-btn half"
                                        >
                                            Half
                                        </button>
                                        <button
                                            @click="setKey(key, 1)"
                                            :class="{
                                                active: fingering[key] === 1,
                                            }"
                                            class="state-btn closed"
                                        >
                                            Closed
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="key-section">
                            <h3 class="text-h6">Thumb Keys</h3>
                            <div class="key-group">
                                <div
                                    class="key-control"
                                    v-for="key in thumbKeys"
                                    :key="key"
                                >
                                    <label>{{ keyLabels[key] }}</label>
                                    <div class="button-group">
                                        <button
                                            @click="setKey(key, 0)"
                                            :class="{
                                                active:
                                                    fingering[key] === 0 ||
                                                    fingering[key] ===
                                                        undefined,
                                            }"
                                            class="state-btn open"
                                        >
                                            Off
                                        </button>
                                        <button
                                            @click="setKey(key, 1)"
                                            :class="{
                                                active: fingering[key] === 1,
                                            }"
                                            class="state-btn closed"
                                        >
                                            On
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="key-section">
                            <h3 class="text-h6">Special Keys</h3>
                            <div class="key-group">
                                <div
                                    class="key-control"
                                    v-for="key in specialKeys"
                                    :key="key"
                                >
                                    <label>{{ keyLabels[key] }}</label>
                                    <div class="button-group">
                                        <button
                                            @click="setKey(key, 0)"
                                            :class="{
                                                active:
                                                    fingering[key] === 0 ||
                                                    fingering[key] ===
                                                        undefined,
                                            }"
                                            class="state-btn open"
                                        >
                                            Off
                                        </button>
                                        <button
                                            @click="setKey(key, 1)"
                                            :class="{
                                                active: fingering[key] === 1,
                                            }"
                                            class="state-btn closed"
                                        >
                                            On
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="actions">
                            <button @click="clearAll" class="action-btn clear">
                                Clear All
                            </button>
                            <button @click="closeAll" class="action-btn fill">
                                Close All Main
                            </button>
                        </div>
                    </div>

                    <!-- Highlights Controls (Right Column) -->
                    <div>
                        <h3 class="text-h6" style="margin-top: 0">
                            Highlights
                        </h3>

                        <div class="key-section">
                            <h4
                                class="text-h7"
                                style="margin-top: 0; margin-bottom: 8px"
                            >
                                Main Holes
                            </h4>
                            <div class="highlight-group">
                                <button
                                    v-for="key in mainHoles"
                                    :key="`hl-${key}`"
                                    @click="toggleHighlight(key)"
                                    :class="{
                                        active: highlights.includes(key),
                                    }"
                                    class="highlight-btn"
                                >
                                    {{ key.toUpperCase() }}
                                </button>
                            </div>
                        </div>

                        <div class="key-section">
                            <h4
                                class="text-h7"
                                style="margin-top: 0; margin-bottom: 8px"
                            >
                                Thumb Keys
                            </h4>
                            <div class="highlight-group">
                                <button
                                    v-for="key in thumbKeys"
                                    :key="`hl-${key}`"
                                    @click="toggleHighlight(key)"
                                    :class="{
                                        active: highlights.includes(key),
                                    }"
                                    class="highlight-btn"
                                >
                                    {{ keyLabels[key] }}
                                </button>
                            </div>
                        </div>

                        <div class="key-section">
                            <h4
                                class="text-h7"
                                style="margin-top: 0; margin-bottom: 8px"
                            >
                                Special Keys
                            </h4>
                            <div class="highlight-group">
                                <button
                                    v-for="key in specialKeys"
                                    :key="`hl-${key}`"
                                    @click="toggleHighlight(key)"
                                    :class="{
                                        active: highlights.includes(key),
                                    }"
                                    class="highlight-btn"
                                >
                                    {{ keyLabels[key] }}
                                </button>
                            </div>
                        </div>

                        <div class="actions">
                            <button
                                @click="clearHighlights"
                                class="action-btn clear"
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Panel: Preview -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Preview</h2>
                <div class="preview-box">
                    <FluteFingering
                        :note="currentNote"
                        :fingering="activeFingering"
                        :orientation="orientation"
                        :bg-color="bgColor"
                        :fill-color="fillColor"
                        :stroke-color="strokeColor"
                        :highlight-color="highlightColor"
                        :highlights="highlights"
                        v-bind="widthHeightProps"
                    />
                </div>

                <div class="size-controls bg-surface-light">
                    <div class="orientation-control">
                        <label>Orientation:</label>
                        <div class="button-group">
                            <button
                                @click="setOrientation('horizontal')"
                                :class="{
                                    active: orientation === 'horizontal',
                                }"
                                class="state-btn"
                            >
                                Horizontal
                            </button>
                            <button
                                @click="setOrientation('vertical')"
                                :class="{ active: orientation === 'vertical' }"
                                class="state-btn"
                            >
                                Vertical
                            </button>
                        </div>
                    </div>

                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="bg-color">Background Color:</label>
                        </div>
                        <input id="bg-color" type="color" v-model="bgColor" />
                    </div>

                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="fill-color">Fill Color:</label>
                        </div>
                        <input
                            id="fill-color"
                            type="color"
                            v-model="fillColor"
                        />
                    </div>

                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="stroke-color">Stroke Color:</label>
                        </div>
                        <input
                            id="stroke-color"
                            type="color"
                            v-model="strokeColor"
                        />
                    </div>

                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="highlight-color"
                                >Highlight Color:</label
                            >
                        </div>
                        <input
                            id="highlight-color"
                            type="color"
                            v-model="highlightColor"
                        />
                    </div>

                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="width-slider"
                                >Width: {{ previewWidth }}px</label
                            >
                            <div class="checkbox-wrapper">
                                <input
                                    id="enable-width"
                                    type="checkbox"
                                    v-model="enableWidth"
                                />
                                <label for="enable-width"
                                    >Pass width prop</label
                                >
                            </div>
                        </div>
                        <input
                            id="width-slider"
                            type="range"
                            v-model.number="previewWidth"
                            min="300"
                            max="800"
                            step="50"
                            :disabled="!enableWidth"
                        />
                    </div>

                    <div class="size-control-group">
                        <div class="size-control-header">
                            <label for="height-slider"
                                >Height: {{ previewHeight }}px</label
                            >
                            <div class="checkbox-wrapper">
                                <input
                                    id="enable-height"
                                    type="checkbox"
                                    v-model="enableHeight"
                                />
                                <label for="enable-height"
                                    >Pass height prop</label
                                >
                            </div>
                        </div>
                        <input
                            id="height-slider"
                            type="range"
                            v-model.number="previewHeight"
                            min="150"
                            max="400"
                            step="25"
                            :disabled="!enableHeight"
                        />
                    </div>
                </div>

                <div class="note-input">
                    <label for="note-name">Note Name:</label>
                    <input
                        id="note-name"
                        v-model="currentNote"
                        type="text"
                        placeholder="e.g., C4, D#5, Bb4"
                    />
                </div>
            </div>
        </div>

        <!-- Examples Section -->
        <div class="examples-section">
            <h2 class="text-h5 text-secondary">Common Fingerings</h2>
            <div class="examples-grid">
                <div
                    v-for="example in examples"
                    :key="example.name"
                    class="example-card"
                >
                    <h3>{{ example.name }}</h3>
                    <FluteFingering
                        :note="example.name"
                        :fingering="example.fingering"
                        :orientation="orientation"
                        v-bind="exampleWidthHeightProps"
                    />
                    <button @click="loadExample(example)" class="load-btn">
                        Load
                    </button>
                </div>
            </div>
        </div>

        <!-- Code Output -->
        <div class="code-section">
            <h2>Component Code</h2>
            <pre><code>{{ componentCode }}</code></pre>
            <button @click="copyCode" class="copy-btn">📋 Copy</button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive } from "vue";
import FluteFingering from "../components/FluteFingering.vue";

interface FingeringState {
    l1?: number;
    l2?: number;
    l3?: number;
    r1?: number;
    r2?: number;
    r3?: number;
    thumbb?: number;
    thumbbflat?: number;
    gsharp?: number;
    trill1?: number;
    trill2?: number;
    csharptrill?: number;
    spatulabflat?: number;
    dsharp?: number;
    lowb?: number;
    lowc?: number;
    lowdflat?: number;
    gizmo?: number;
}

const mainHoles = ["l1", "l2", "l3", "r1", "r2", "r3"];
const thumbKeys = ["thumbb", "thumbbflat"];
const specialKeys = [
    "gsharp",
    "trill1",
    "trill2",
    "csharptrill",
    "spatulabflat",
    "dsharp",
    "lowb",
    "lowc",
    "lowdflat",
    "gizmo",
];

const keyLabels: Record<string, string> = {
    l1: "L1",
    l2: "L2",
    l3: "L3",
    r1: "R1",
    r2: "R2",
    r3: "R3",
    thumbb: "Thumb B",
    thumbbflat: "Thumb Bb",
    gsharp: "G#",
    trill1: "Trill 1",
    trill2: "Trill 2",
    csharptrill: "C# Trill",
    spatulabflat: "Spatula Bb",
    dsharp: "D#",
    lowb: "Low B",
    lowc: "Low C",
    lowdflat: "Low Db",
    gizmo: "Gizmo",
};

const fingering = reactive<FingeringState>({
    l1: 1,
    l2: 1,
    l3: 1,
    r1: 1,
    r2: 1,
    r3: 1,
});

const currentNote = ref("C4");
const previewWidth = ref(500);
const previewHeight = ref(200);
const orientation = ref<"horizontal" | "vertical">("horizontal");
const enableWidth = ref(true);
const enableHeight = ref(true);
const bgColor = ref("transparent");
const fillColor = ref("black");
const strokeColor = ref("black");
const highlightColor = ref("#FFD700");
const highlights = ref<string[]>([]);

const activeFingering = computed(() => {
    const result: FingeringState = {};
    for (const [key, value] of Object.entries(fingering)) {
        if (value !== undefined && value !== 0) {
            result[key as keyof FingeringState] = value;
        }
    }
    return result;
});

const widthHeightProps = computed(() => {
    const props: Record<string, number> = {};
    if (enableWidth.value) {
        props.width = previewWidth.value;
    }
    if (enableHeight.value) {
        props.height = previewHeight.value;
    }
    return props;
});

const exampleWidthHeightProps = computed(() => {
    const props: Record<string, number> = {};
    if (enableWidth.value) {
        props.width = 180;
    }
    if (enableHeight.value) {
        props.height = 100;
    }
    return props;
});

const componentCode = computed(() => {
    const fingeringStr = JSON.stringify(activeFingering.value, null, 2)
        .split("\n")
        .map((line, i) => (i === 0 ? line : "  " + line))
        .join("\n");

    const props: string[] = [`  note="${currentNote.value}"`];
    props.push(`  :fingering="${fingeringStr}"`);
    props.push(`  orientation="${orientation.value}"`);
    props.push(`  bg-color="${bgColor.value}"`);
    props.push(`  fill-color="${fillColor.value}"`);
    props.push(`  stroke-color="${strokeColor.value}"`);
    if (highlights.value.length > 0) {
        props.push(
            `  :highlights="${JSON.stringify(highlights.value).replace(/"/g, "'")}"`,
        );
        props.push(`  highlight-color="${highlightColor.value}"`);
    }

    if (enableWidth.value) {
        props.push(`  :width="${previewWidth.value}"`);
    }
    if (enableHeight.value) {
        props.push(`  :height="${previewHeight.value}"`);
    }

    const propsString = props.join("\n");
    return `<FluteFingering\n${propsString}\n/>`;
});

function setKey(key: string, value: number) {
    fingering[key as keyof FingeringState] = value;
}

function setOrientation(newOrientation: "horizontal" | "vertical") {
    orientation.value = newOrientation;
}

function clearAll() {
    for (const key of [...mainHoles, ...thumbKeys, ...specialKeys]) {
        fingering[key as keyof FingeringState] = 0;
    }
}

function toggleHighlight(key: string) {
    const index = highlights.value.indexOf(key);
    if (index > -1) {
        highlights.value.splice(index, 1);
    } else {
        highlights.value.push(key);
    }
}

function clearHighlights() {
    highlights.value = [];
}

function closeAll() {
    for (const key of mainHoles) {
        fingering[key as keyof FingeringState] = 1;
    }
}

interface Example {
    name: string;
    fingering: FingeringState;
}

const examples: Example[] = [
    {
        name: "C4",
        fingering: { l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1 },
    },
    {
        name: "D4",
        fingering: { l2: 1, l3: 1, r1: 1, r2: 1, r3: 1 },
    },
    {
        name: "E4",
        fingering: { l3: 1, r1: 1, r2: 1, r3: 1 },
    },
    {
        name: "F4",
        fingering: { r1: 1, r2: 1, r3: 1 },
    },
    {
        name: "G4",
        fingering: { r2: 1, r3: 1 },
    },
    {
        name: "A4",
        fingering: { r3: 1 },
    },
    {
        name: "B4",
        fingering: { thumbb: 1 },
    },
    {
        name: "C5",
        fingering: { l2: 1 },
    },
    {
        name: "G#3",
        fingering: { l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, gsharp: 1 },
    },
    {
        name: "Low C",
        fingering: { l1: 1, l2: 1, l3: 1, r1: 1, r2: 1, r3: 1, lowc: 1 },
    },
];

function loadExample(example: Example) {
    clearAll();
    Object.assign(fingering, example.fingering);
    currentNote.value = example.name;
}

async function copyCode() {
    try {
        await navigator.clipboard.writeText(componentCode.value);
        alert("Code copied to clipboard!");
    } catch (err) {
        console.error("Failed to copy:", err);
    }
}
</script>

<style scoped>
.debug-flute-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 20px;
}

.layout {
    display: grid;
    grid-template-columns: 400px 1fr;
    gap: 30px;
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
}

.key-section {
    margin-bottom: 25px;
}

.key-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.key-control {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border-radius: 4px;
}

.key-control label {
    font-weight: 600;
    min-width: 80px;
}

.button-group {
    display: flex;
    gap: 5px;
}

.state-btn {
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
}

.state-btn:hover {
    opacity: 0.8;
}

.state-btn.active {
    font-weight: bold;
}

.actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
    padding-top: 20px;
}

.action-btn {
    flex: 1;
    padding: 10px;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.action-btn:hover {
    opacity: 0.9;
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px;
    border-radius: 6px;
    min-height: 250px;
}

.size-controls,
.note-input {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
}

.size-controls label,
.note-input label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 14px;
}

.size-control-group {
    margin-bottom: 20px;
}

.size-control-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.size-control-header label {
    margin-bottom: 0;
}

.checkbox-wrapper {
    display: flex;
    align-items: center;
    gap: 5px;
}

.checkbox-wrapper input[type="checkbox"] {
    margin: 0;
}

.checkbox-wrapper label {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 0;
    opacity: 0.7;
}

.size-controls input[type="range"] {
    width: 100%;
    margin-bottom: 0;
}

.size-controls input[type="range"]:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.orientation-control {
    margin-bottom: 20px;
    padding-bottom: 15px;
}

.orientation-control label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 14px;
}

.orientation-control .button-group {
    display: flex;
    gap: 5px;
}

.orientation-control .state-btn {
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    flex: 1;
    text-align: center;
    font-weight: 500;
}

.orientation-control .state-btn:hover {
    opacity: 0.8;
}

.orientation-control .state-btn.active {
    font-weight: bold;
}

.note-input input[type="text"] {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    font-size: 14px;
}

.note-input input[type="text"]:focus {
    outline: none;
}

.examples-section {
    margin: 40px 0;
}

.examples-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 15px;
    margin-top: 15px;
}

.example-card {
    border-radius: 8px;
    padding: 15px;
    text-align: center;
    transition: all 0.2s;
}

.example-card:hover {
    transform: translateY(-2px);
}

.example-card h3 {
    margin: 0 0 10px 0;
    font-size: 16px;
}

.load-btn {
    margin-top: 10px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}

.load-btn:hover {
    opacity: 0.9;
}

.code-section {
    position: relative;
    padding: 20px;
    border-radius: 8px;
    margin-top: 40px;
}

.code-section h2 {
    margin-top: 0;
}

.code-section pre {
    margin: 0;
    overflow-x: auto;
}

.code-section code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

.copy-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 600;
    transition: background 0.2s;
}

.copy-btn:hover {
    opacity: 0.9;
}

.highlight-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 16px;
}

.highlight-btn {
    padding: 6px 12px;
    border: 2px solid #ccc;
    border-radius: 4px;
    background: white;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    transition: all 0.2s;
    color: #333;
}

.highlight-btn:hover {
    border-color: #999;
    background-color: #f5f5f5;
}

.highlight-btn.active {
    background: #ffd700;
    border-color: #daa520;
    color: #000;
    font-weight: 600;
}

#bg-color,
#fill-color,
#stroke-color,
#highlight-color {
    width: 100%;
    height: 40px;
    border: 1px solid #ccc;
    border-radius: 4px;
    cursor: pointer;
    padding: 4px;
}

@media (max-width: 900px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
